import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { PageTitle } from '../PageTitle';
import moment from 'moment-timezone';
import { Message } from '../Message';
import { AlertStatusesService, DeviceDataOverrideService, EntityService, TimeFormattingService } from "../../services";
import { DataTypeDisplayValue } from '../DataTypeDisplayValue';
import { connect } from 'react-redux';
import { EntityDetailModal } from "../Entity";
import i18next from "i18next";
import ReactTable from "react-table";
import "react-table/react-table.css";
import FontAwesomeIcon from '@fortawesome/react-fontawesome';
import faPencilAlt from '@fortawesome/fontawesome-free-solid/faPencilAlt';
import faExternalLinkAlt from '@fortawesome/fontawesome-free-solid/faExternalLinkAlt';
import { FormatHelper } from '../../helpers';
import EventGraph from '../Graphs/event_graph';
import StateEventGraph from '../Graphs/state_event_graph';
import { FeatureAccess } from "../FeatureAccess";
import { AlertHistory, NotificationHistory, TimelineView } from "./alert_detail_shared";
import config from "config";

import { findMinDatapointFromAlert, findMaxDatapointFromAlert, findEndTime, findStartTime, convertThresholdExceededEventsToDeviceReceivedData } from "./event_data_helpers";

const AlertDetail = ({ isMobile, alert, updateStatus, updateNote, addResponse }) => {
  const [entity, setEntity] = useState(null);
  const [rangeData, setRangeData] = useState({});
  const [thresholdData, setThresholdData] = useState({});
  const [alertExceededTime, setAlertExceededTime] = useState(null);
  const [availableAlertStatuses, setAvailableAlertStatuses] = useState(null);
  const [showEntityDetailModal, setShowEntityDetailModal] = useState(false);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [showAddCorrectiveActionModal, setShowAddCorrectiveActionModal] = useState(false);
  const [localSuccessMessage, setLocalSuccessMessage] = useState(null);
  const [localErrorMessage, setLocalErrorMessage] = useState(null);
  const [editNote, setEditNote] = useState(false);
  const [noteValue, setNoteValue] = useState(alert.alertNotes ? alert.alertNotes : "");

  const timezone = alert.originatingGrouping.timezone;
  const chartData = convertThresholdExceededEventsToDeviceReceivedData(alert, timezone);
  const containsProvisionalData = alert.thresholdExceededEvents.filter(tee => tee.provisional == true).length > 0;

  useEffect(() => {
    if (alert.thresholdExceededEvents && alert.thresholdExceededEvents.length > 1) {
      const thresholdExceededEvents = alert.thresholdExceededEvents;
      const deviceTypeToken = thresholdExceededEvents?.length > 0 ? thresholdExceededEvents[0]?.triggerData?.deviceDataType?.token : "";
      const fnToApply = DeviceDataOverrideService.getOverrideFunctionForDeviceTypeToken(deviceTypeToken);
      setThresholdData({
          value: fnToApply(alert.triggerThreshold.displayableEvaluationNumericValue ? alert.triggerThreshold.displayableEvaluationNumericValue : alert.triggerThreshold.displayableEvaluationStringValue),
      });

      // Events are sorted backwards
      const startTime = findStartTime(thresholdExceededEvents.sort((entryA, entryB) => entryB.occurredAt - entryA.occurredAt));
      const startTimeUTCOffset = timezone ? moment(startTime).tz(timezone).utcOffset() : moment(startTime).utcOffset();
      const endTime = findEndTime(thresholdExceededEvents.sort((entryA, entryB) => entryB.occurredAt - entryA.occurredAt));
      const endTimeUTCOffset = timezone ? moment(endTime).tz(timezone).utcOffset() : moment(endTime).utcOffset();
      const halfReadingDuration = Math.round((endTime - startTime) / 1000 / thresholdExceededEvents.length / 2) + 30;
      setRangeData({
          start: Math.round(startTime / 1000) - halfReadingDuration + startTimeUTCOffset * 60,
          end: Math.round(endTime / 1000) + halfReadingDuration + endTimeUTCOffset * 60
      });

      setAlertExceededTime(moment(endTime).diff(moment(startTime), 'minutes') + i18next.t('messages.minutes'));
    }

    AlertStatusesService.listAll()
      .then(({ alertStatuses }) => setAvailableAlertStatuses(alertStatuses))
      .catch((error) => {
        setLocalErrorMessage(i18next.t('messages.could-not-load-event-statuses'));
        console.error(error);
      });

    EntityService.getBySerialNumber(alert.originatingMeasure.entitySerialNumber)
      .then(({entity}) => setEntity(entity))
      .catch((error) => {
        setLocalErrorMessage('Could not load entity.');
        console.error(error);
      });

    }, [alert]);

  const saveNote = (note) => {
    addResponse({
      note: note,
      responseType: {id: 1}
    });
  };

  const saveCorrectiveAction = (correctiveAction) => {
    addResponse({
      note: correctiveAction,
      responseType: {id: 2}
    });
  };

  const messageDismissCallback = () => {
    setLocalErrorMessage(null);
    setLocalSuccessMessage(null);
  };

  if (availableAlertStatuses && entity) {
    let minDatapoint = findMinDatapointFromAlert(alert)
    if (minDatapoint?.numericValue != null) {
        minDatapoint = {
            ...minDatapoint,
            numericValue: DeviceDataOverrideService.getOverrideFunctionForDeviceTypeToken(minDatapoint.deviceDataType.token)(minDatapoint.numericValue),
        };
    }
    let maxDatapoint = findMaxDatapointFromAlert(alert);      
    if (maxDatapoint?.numericValue != null) {
        maxDatapoint = {
            ...maxDatapoint,
            numericValue: DeviceDataOverrideService.getOverrideFunctionForDeviceTypeToken(maxDatapoint.deviceDataType.token)(maxDatapoint.numericValue),
        };
    }

    return (
        <div>
          {!isMobile && <div className="row"><PageTitle title={ i18next.t('pages.event-details.header') } /></div>}

          <Message
            messages={{ state: { successMessage: localSuccessMessage } }}
            errorMessage={localErrorMessage}
            messageDismissCallback={messageDismissCallback}
          />

          <LevelStatusRow
            isMobile={isMobile}
            entityDisplayName={alert.originatingMeasure.entityDisplayName}
            alert={alert}
            containsProvisionalData={containsProvisionalData}
            alertExceededTime={alertExceededTime}
            availableAlertStatuses={availableAlertStatuses}
            showEntityDetailModal={() => setShowEntityDetailModal(true)}
            updateStatus={updateStatus}
          />

          <MeasureStatusArea
            isMobile={isMobile}
            originatingMeasure={alert.originatingMeasure}
            alert={alert}
            chartData={chartData} 
            thresholdData={thresholdData}
            rangeData={rangeData}
            entityStatusToken={alert.originatingMeasure.entityStatusToken}
            showEntityDetailModal={() => setShowEntityDetailModal(true)}
            triggerAddNote={() => { setNoteValue(null); setShowAddNoteModal(true); }}
            triggerAddCorrectiveAction={() => { setNoteValue(null); setShowAddCorrectiveActionModal(true); }}
            noteEdit={editNote}
            noteToggleEdit={() => setEditNote(!editNote)}
            noteValue={noteValue}
            noteUpdateNoteValue={setNoteValue}
            noteSubmitNote={updateNote}
            minDatapoint={minDatapoint} 
            maxDatapoint={maxDatapoint}
            availableAlertStatuses={availableAlertStatuses}
            updateStatus={updateStatus}
          />

          <FeatureAccess feature='ENABLE_ALERT_TIMELINE_VIEW'>
            <div hasfeature="true" className="op-content-box alert-details-wrapper">
              <TimelineView alert={alert} minDatapoint={minDatapoint} maxDatapoint={maxDatapoint} />
            </div>
          </FeatureAccess>

          { !isMobile && <ThresholdExceededArea alert={alert} deviceTypeToken={entity.entityTypeToken} /> }

          <FeatureAccess feature='ENABLE_ALERT_TIMELINE_VIEW'>
            <div hasfeature="true"></div>
            <div hasfeature="false" className="op-content-box alert-details-wrapper">
              <AlertHistory alertHistory={alert.history} timezone={timezone} />
              <NotificationHistory alertNotificationHistory={alert.alertNotificationHistory} timezone={timezone} />
            </div>
          </FeatureAccess>

          <EntityDetailModal
            isMobile={isMobile}
            entity={entity}
            show={showEntityDetailModal}
            onHide={() => setShowEntityDetailModal(false)}
          />

          <AddNoteModal 
            isMobile={isMobile}
            show={showAddNoteModal}
            currentValue={noteValue}
            onHide={() => setShowAddNoteModal(false)}
            onValueChange={setNoteValue}
            onSave={() => saveNote(noteValue)}
          />

          <AddCorrectiveActionModal 
            isMobile={isMobile}
            show={showAddCorrectiveActionModal}
            currentValue={noteValue}
            onHide={() => setShowAddCorrectiveActionModal(false)}
            onValueChange={setNoteValue}
            onSave={() => saveCorrectiveAction(noteValue)}
          />
        </div>
      );
    } else {
      return (<div className="loader">{ i18next.t('loading') }</div>);
    }
};

const LevelStatusRow = ({isMobile, entityDisplayName, alert, containsProvisionalData, alertExceededTime, availableAlertStatuses, showEntityDetailModal, updateStatus}) => {
  return (
    <div className={`op-content-box alert-highlights ${alert.alertLevel.name.toLowerCase()}`}>
      <div className={`row alert-level ${alert.alertLevel.name.toLowerCase()}`}>
        <p>
          { i18next.t(`alert-levels.${alert.alertLevel.token}`).toUpperCase() + ' - ' + alert.alertConfiguration.name + (containsProvisionalData ? ' (PROVISIONAL DATA)' : '') }
        </p>
      </div>

      <div className="row">
        <div className="col-xs-12 col-sm-12 col-md-12 col-lg-3">
          <div className="row row-no-gutters">
            <div className="col-xs-2 col-sm-2 col-md-2 col-lg-2">
              <img src={`${config.WEB_BASE_URL}/images/icon/area.svg`} height="34" />
            </div>
            <div onClick = {showEntityDetailModal} className="col-xs-10 col-sm-10 col-md-10 col-lg-10">
              <p className = "deviceDetailLink">
                {entityDisplayName}
              </p>
              <p style={{fontSize: '14px', fontWeight: 500}}>
                <b>{alert.triggeringTag.name} </b> : {alert.originatingGrouping.name}
              </p>
            </div>
          </div>
        </div>

        <div className="col-xs-12 col-sm-12 col-md-12 col-lg-3">
          <div className="row row-no-gutters">
            <div className="col-xs-2 col-sm-2 col-md-2 col-lg-2">
              <img width={'34'} height={'34'} src={`${config.WEB_BASE_URL}/images/icon/sensor_${alert.thresholdClassification.token}.svg`}/>
            </div>
            <div className="col-xs-10 col-sm-10 col-md-10 col-lg-10">
              <p style={{marginBottom: 0}}> {alert.thresholdClassification.name}</p>
              {alertExceededTime == null ? <br/> :
                <p style={{fontSize: '14px', fontWeight: 500}}>
                  { i18next.t('pages.event-details.threshold') }
                  <b>{alert.triggerThreshold.name}</b>{ i18next.t('pages.event-details.exceeded-for') }
                  <b>{alertExceededTime}</b> { i18next.t('pages.event-details.limit-is') } 
                  <b>{`${Math.round(alert.alertConfigurationAction.duration/60)} ${i18next.t('messages.minutes')}`}</b>
                </p>
              }
            </div>
          </div>
        </div>

        <div className="col-xs-12 col-sm-12 col-md-12 col-lg-3">
          <div className="row row-no-gutters">
            <div className="col-xs-2 col-sm-2 col-md-2 col-lg-2">
              <img src={`${config.WEB_BASE_URL}/images/icon/clock.svg`} height="34" />
            </div>
            <div className="col-xs-10 col-sm-10 col-md-10 col-lg-10">
              <span>
                <p style={{fontSize: '14px', fontWeight: 500}}>
                  { i18next.t('pages.event-details.reported-at') }<b> {TimeFormattingService.renderTimeOnDate(alert.occurredAt, alert.originatingGrouping.timezone ? alert.originatingGrouping.timezone : null)} </b>
                </p>
              </span>
            </div>
          </div>
        </div>
          
        <div className="col-xs-12 col-sm-12 col-md-12 col-lg-3">
          { !isMobile && <ActionButtonArea currentAlertStatus={alert.alertStatus} availableAlertStatuses={availableAlertStatuses} updateStatus={updateStatus} /> }
        </div>
      </div>
    </div>
  );
};

const ActionButtonArea = ({currentAlertStatus, availableAlertStatuses, updateStatus}) => (
  <div className='row row-no-gutters'>
    <div className="col-xs-12 col-md-4 col-lg-12">
      <div className="col-xs-12 col-md-12 col-lg-12 status-string">
        <span><p>{ i18next.t('pages.alerts.event-status') }: { i18next.t(`alert-statuses.${currentAlertStatus.token}`) }</p></span>
      </div>

      {
        (currentAlertStatus.token !== 'resolved' && currentAlertStatus.token !== 'auto_resolved') &&
        <div className="col-xs-10 col-md-10 col-xs-offset-2 col-md-offset-2">
          <ul className="list-inline add-margin-bottom">
          {
              availableAlertStatuses
                .filter(status => status.token === 'acknowledged' || status.token === 'resolved')
                .map((alertStatus, index) => ( alertStatus.token === 'acknowledged' ? (
                    currentAlertStatus.token === 'acknowledged' ? (
                      <li key={index}>
                        <button className={'btn alert-status-control btn-sm is-active'}>
                          { i18next.t('alert-status-change-button-labels.acknowledged') }
                        </button>
                      </li>
                    ) : (
                  <li key={index}>
                    <button key={alertStatus.id} className={'btn alert-status-control btn-sm'} onClick={() => updateStatus(alertStatus)} >
                      { i18next.t('alert-status-change-button-labels.acknowledge') }
                    </button>
                  </li>
                  )
                ) : (
                <li key={index}>
                  <button key={alertStatus.id} className={'btn alert-status-control btn-sm'} onClick={() => updateStatus(alertStatus)} >
                    { i18next.t('alert-status-change-button-labels.resolve') }
                  </button>
                </li>
                )
              )
            )}
        </ul>
      </div>
    }
    </div>
  </div>
);

const MeasureStatusArea = ({isMobile, originatingMeasure, alert, chartData, thresholdData, rangeData, entityStatusToken, showEntityDetailModal, minDatapoint, maxDatapoint, triggerAddNote, triggerAddCorrectiveAction, noteEdit, noteToggleEdit, noteValue, noteUpdateNoteValue, noteSubmitNote, availableAlertStatuses, updateStatus}) => {
  const eventGraph = (chartData && chartData.length > 0 && chartData[0].state) ?
    <StateEventGraph
      data={chartData}
      timezone={alert.originatingGrouping.timezone ? alert.originatingGrouping.timezone : null}
      threshold={thresholdData}
      swapValues={alert.deviceDataType.token === 'door_open'}
      trueValue={i18next.t(`data-string-display.${alert.deviceDataType.token}.true`)}
      falseValue={i18next.t(`data-string-display.${alert.deviceDataType.token}.false`)}
      eventRange={rangeData}
      width={isMobile ? 360 : 640}
      height={260}
      id="state-event-graph"
    />
    :
    <EventGraph
      data={chartData}
      threshold={thresholdData}
      yAxisLabel={i18next.t([`device-data-display-types.${DeviceDataOverrideService.getOverrideForDisplayToken(alert.deviceDataType.token)}`, `device-data-types.${alert.deviceDataType.token}`])}
      eventRange={rangeData}
      width={isMobile ? 360 : 640}
      height={260}
      id="event-graph"
    />;

  let measureLastReceivedData = originatingMeasure.lastReceivedData;
  if (measureLastReceivedData?.numericValue != null) {
      measureLastReceivedData = { ...measureLastReceivedData, numericValue: DeviceDataOverrideService.getOverrideFunctionForDeviceTypeToken(measureLastReceivedData.deviceDataType.token)(measureLastReceivedData.numericValue) };
  }
  const lastReadAt = TimeFormattingService.renderTimeOnDate(measureLastReceivedData.lastUpdatedAt, alert.originatingGrouping.timezone ? alert.originatingGrouping.timezone : null);
  const measureIndicationIconURL = originatingMeasure.thresholdCurrentlyExceeded != null && originatingMeasure.thresholdCurrentlyExceeded.thresholdClassification ? 
              `${config.WEB_BASE_URL}/images/icon/sensor_${originatingMeasure.thresholdCurrentlyExceeded.thresholdClassification.token}.svg` :
              `${config.WEB_BASE_URL}/images/icon/sensor_${measureLastReceivedData.token}.svg`;

  return (
      <div className="op-content-box alert-details-wrapper">
        <h4 style={{color: "#7F7F7F"}} className="center-block center-title">{ i18next.t('pages.event-details.current-status') }</h4>

        <div className="row">
          <div className="col-xs-12 col-sm-6 col-md-4 col-lg-4 col-xs-offset-0 col-sm-offset-0">
            <div className="row row-no-gutters">
              <div className="col-xs-2 col-sm-2 col-md-2 col-lg-2">
                <img src={`${config.WEB_BASE_URL}/images/icon/sensor_${entityStatusToken}.svg`} height="34" />
              </div>
              <div className="col-xs-10 col-sm-10 col-md-10 col-lg-10">
                <p style={{marginBottom: 0}}>
                  <b>
                    { i18next.t(`device-statuses.${entityStatusToken}`) } 
                    &nbsp;/&nbsp;
                    { i18next.t(`connectivity-status-data-string-display.${originatingMeasure.online ? 'Online' : 'Offline'}`) }
                  </b>
                </p>
              </div>
            </div>
          </div>

          <br className="visible-xs-block"/>
          <br className="visible-xs-block"/>

          <div className="col-xs-12 col-sm-6 col-md-4 col-lg-4 col-xs-offset-0 col-sm-offset-0">
            <div className="row row-no-gutters">
              <div className="col-xs-2 col-sm-2 col-md-2 col-lg-2">
                <img src={measureIndicationIconURL} height="34" />
              </div>
              <div className="col-xs-10 col-sm-10 col-md-10 col-lg-10">
                <p style={{marginBottom: 0}}><b><DataTypeDisplayValue value={measureLastReceivedData} deviceTypeToken={originatingMeasure.entityTypeToken}/></b></p>
                <p style={{fontSize: '14px', fontWeight: 500}}>{ i18next.t('pages.event-details.last-read-at') } <b> { lastReadAt } </b></p>
              </div>
            </div>
          </div>
          
          <div onClick = {showEntityDetailModal} className="col-xs-12 col-sm-6 col-md-4 col-lg-4 col-xs-offset-0 col-sm-offset-0">
            <div className="row row-no-gutters">
              <div className="col-xs-2 col-sm-2 col-md-2 col-lg-2">
                <img src={`${config.WEB_BASE_URL}/images/icon/info_icon.svg`} height="34" />
              </div>
              <div className="col-xs-10 col-sm-10 col-md-10 col-lg-10">
                <p className = "deviceDetailLink"><b>{ i18next.t('pages.event-details.asset-details') }</b><FontAwesomeIcon style ={{marginLeft: "5px"}} icon={faExternalLinkAlt}/></p>
              </div>
            </div>
          </div>
        </div>

        <br className="visible-xs-block"/>

        <div className="row row-no-gutters">
          <div className="col-xs-12 col-sm-12 col-md-12 col-lg-7">
            <h4 style={{color: "#7F7F7F"}} className="center-block center-title">{ i18next.t('pages.event-details.readings-exceeding-threshold') }</h4>
            { alert.truncated ? <div style={{color: "#7F1F1F", paddingBottom: 10}}>* Due to the duration of this event the data has been truncated.</div> : null }
            { minDatapoint ? <span>Min value: {<DataTypeDisplayValue value={minDatapoint} />}</span> : null }
            &nbsp;
            { maxDatapoint ? <span>Max value: {<DataTypeDisplayValue value={maxDatapoint} />}</span> : null }
            { eventGraph }
          </div>
        </div>

        { isMobile && <ActionButtonArea currentAlertStatus={alert.alertStatus} availableAlertStatuses={availableAlertStatuses} updateStatus={updateStatus} /> }

        <FeatureAccess feature='ENABLE_GEN2_ALERT_NOTES'>
            <DualNoteAndCorrectiveActionArea
                hasfeature='true'
                triggerAddNote={triggerAddNote}
                triggerAddCorrectiveAction={triggerAddCorrectiveAction}
            />
            <NotesArea 
                hasfeature='false'
                alertNotes={alert.alertNotes}
                edit={noteEdit} 
                toggleEdit={noteToggleEdit} 
                noteValue={noteValue}
                updateNoteValue={noteUpdateNoteValue}
                submitNote={noteSubmitNote}
            />
        </FeatureAccess>
      </div>
  );
};

const AddNoteModal = ({isMobile, show, currentValue, onValueChange, onHide, onSave}) => {
  return (
    <Modal className={isMobile ? "modal-device-detail-mobile" : "modal-device-detail"} show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          <div>Add Note</div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <input
            value={currentValue}
            onChange={(val) => onValueChange(val.target.value)}
            type="textarea"
            name="notes"
            maxLength = "100"
            rows="4"
            style={{width: '75%', height: '65px', paddingBottom: '40px', color: 'light-gray'}}
            placeholder={ i18next.t('form-labels.enter-text-here') }
        />
      </Modal.Body>
      <Modal.Footer>
        <a className="btn btn-default" onClick={onSave}>{i18next.t('save')}</a>
        <Button onClick={onHide}>{ i18next.t('form-labels.cancel') }</Button>
      </Modal.Footer>
    </Modal>
  );
};

const AddCorrectiveActionModal = ({isMobile, show, currentValue, onHide, onValueChange, onSave}) => {
  return (
    <Modal className={isMobile ? "modal-device-detail-mobile" : "modal-device-detail"} show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          <div>Add Corrective Action</div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <input
            value={currentValue}
            onChange={(val) => onValueChange(val.target.value)}
            type="textarea"
            name="notes"
            maxLength = "100"
            rows="4"
            style={{width: '75%', height: '65px', paddingBottom: '40px', color: 'light-gray'}}
            placeholder={ i18next.t('form-labels.enter-text-here') }
        />
      </Modal.Body>
      <Modal.Footer>
        <a className="btn btn-default" onClick={onSave}>{i18next.t('save')}</a>
        <Button onClick={onHide}>{ i18next.t('form-labels.cancel') }</Button>
      </Modal.Footer>
    </Modal>
  );
};

const DualNoteAndCorrectiveActionArea = ({triggerAddNote, triggerAddCorrectiveAction}) => {
  return (
    <div className="row row-no-gutters">
      <div className="col-xs-10 col-sm-10 col-md-6 col-lg-4 col-lg-offset-2 col-xs-offset-2 col-sm-offset-2">
        <button onClick = {triggerAddNote} className = "btn alert-status-control btn-sm">Add Note</button>
      </div>

      <div className="col-xs-10 col-sm-10 col-md-6 col-lg-6 col-xs-offset-2 col-sm-offset-2">
        <button onClick = {triggerAddCorrectiveAction} className = "btn alert-status-control btn-sm">Add Corrective Action</button>
      </div>
    </div>
  );
}

const ThresholdExceededAreaNameHeader = ({threshold}) => {
  const thresholdMeasure = threshold.deviceDataClassification ? i18next.t(`device-data-classifications.${threshold.deviceDataClassification?.token}`) : i18next.t(`device-data-types.${threshold.deviceDataType?.token}`);
  return (
    <div>
      {threshold.name} <br/> 
      <img src={`${config.WEB_BASE_URL}/images/icon/sensor_${threshold.thresholdClassification.token}.svg`} height="16" />&nbsp;
      {thresholdMeasure}&nbsp;
      <span className="threshold-evaluation">{threshold.thresholdEvaluationType.shortForm}</span>&nbsp;
      {FormatHelper.formatMeasurement(threshold)}
    </div>
  );
};

const ThresholdExceededArea = ({alert, deviceTypeToken}) => {
  const columnConfiguration = [
    {
      Header: i18next.t('table-headers.threshold-name'),
      id: "alertThresholdName",
      accessor: _ => <ThresholdExceededAreaNameHeader threshold={alert.triggerThreshold} />,
      sortable: false,
      minResizeWidth: 50
    },
    {
      Header: i18next.t('table-headers.reading-value'),
      id: "alertStatus",
      accessor:  "triggerData",
      Cell: cell => {
        let data = cell.original.triggerData;
        if (data?.numericValue != null) {
            data = {
                ...data,
                numericValue: DeviceDataOverrideService.getOverrideFunctionForDeviceTypeToken(data.deviceDataType.token)(data.numericValue),
            };
        }
        return (cell.original.provisional ? 'Provisional Data' : <DataTypeDisplayValue value={data} deviceTypeToken={deviceTypeToken}/>)
      },
      sortable: true,
      sortMethod: (a, b) => {
        if (a.numericValue && b.numericValue) {
          return a.numericValue - b.numericValue;
        } else if (a.stringValue && b.stringValue) {
          return a.stringValue.toLowerCase() - b.stringValue.toLowerCase();
        }
        return 0;
      },
      minResizeWidth: 50
    },
    {
      Header: i18next.t('table-headers.reading-taken-at'),
      id: "alertReadingTaken",
      accessor: row => row.provisional ? row.occurredAt : row.triggerData?.deviceTime,
      Cell: cell => <span>{TimeFormattingService.renderNormalTime(cell.original.provisional ? cell.original.occurredAt : cell.original.triggerData?.deviceTime, alert.originatingGrouping.timezone)}</span>,
      sortable: true,
      minResizeWidth: 50
    },
    {
      Header: i18next.t('table-headers.reading-processed-at'),
      id: "alertReadingProcessed",
      accessor: "occurredAt",
      Cell: cell => <span>{TimeFormattingService.renderNormalTime(cell.original.occurredAt, alert.originatingGrouping.timezone)}</span>,
      sortable: true,
      minResizeWidth: 50
    }
  ];

  return (
    <div className="op-content-box">
      <h4 style={{color: "#7F7F7F"}}>{ i18next.t('pages.event-details.thresholds-exceeded') }</h4>
      
      { alert.truncated ? <div style={{color: "#7F1F1F", paddingBottom: 10}}>* Due to the duration of this event the data has been truncated.</div> : null }

      <div className="row">
        <div className="col-sm-12">
          <div className="react-table-custom">

            <ReactTable
              className="-striped -highlight"
              data={alert.thresholdExceededEvents}
              showPaginationTop
              defaultPageSize={5}
              totalElements={alert.thresholdExceededEvents.length}
              defaultSorted={[{ id: 'alertReadingTaken', desc: true }]}
              columns={columnConfiguration}
            />

          </div>
        </div>
      </div>
    </div>
  );
};

const NotesArea = ({alertNotes, edit, toggleEdit, noteValue, updateNoteValue, submitNote}) => {  
  return (
    <div className="row" style={{marginTop: 20}}>
      <div className="col-xs-12 col-sm-12 col-md-8 col-lg-6">
        <div className="card-data-cell remove-border-left">
          <div className="row">
            <div className="col-xs-11 col-sm-11 cold-md-11 col-lg-11">
              <h4 style={{color: "#7F7F7F"}} className="center-block">{ i18next.t('pages.event-details.notes') }</h4>
            </div>
            <div className="col-xs-1 col-sm-1 cold-md-1 col-lg-1 text-right">
              {!edit ? <FontAwesomeIcon icon={faPencilAlt} size='sm' onClick = {toggleEdit}/> : ''}
            </div>
          </div>

          <hr className="remove-margin-top" style={{marginBottom: 10}}/>
          
          {
            edit ?
            <div>
              <input
                value = {noteValue}
                onChange={(val) => updateNoteValue(val.target.value)}
                type="textarea"
                name="notes"
                maxLength = "100"
                rows="4"
                style={{width: '75%', height: '65px', paddingBottom: '40px', color: 'light-gray'}}
                placeholder={ i18next.t('form-labels.enter-text-here') }
              />
              <br/>
              <button onClick = {submitNote} className = "btn btn-default btn-sm" style={{margin: '10px 5px 20px 0px', display:'inline-block'}}> { i18next.t('form-labels.save') } </button>
            </div>
            :
            alertNotes ?
              <div style={{marginBottom: 20}}><p>{alertNotes}</p></div>
              :
              <div style={{marginBottom: 20}}><p style={{color: "#7F7F7F"}}><i>{ i18next.t('messages.no-notes-to-display') }</i></p></div>
          }
        </div>
      </div>
    </div>
  );
};

const mapUserToProps = ({ user }) => ({ me: user.me });

export default connect(mapUserToProps)(AlertDetail);
